'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { X, WarningCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface TechnicalBreakModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TechnicalBreakModal({ isOpen, onClose }: TechnicalBreakModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Mobile: bottom sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className={cn(
              'fixed bottom-0 left-0 right-0 md:hidden',
              'rounded-t-3xl bg-[var(--surface-primary)] p-6 pb-8',
              'shadow-[0_-8px_30px_rgba(0,0,0,0.12)]'
            )}
          >
            <div className="mb-3 flex justify-center">
              <div className="h-1 w-10 rounded-full bg-[var(--border-default)]" />
            </div>
            <ModalBody onClose={onClose} />
          </motion.div>

          {/* Desktop: centered modal */}
          <div className="hidden md:flex fixed inset-0 items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 16 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className={cn(
                'relative w-full max-w-sm rounded-3xl',
                'bg-[var(--surface-primary)] p-8',
                'shadow-[0_16px_48px_rgba(0,0,0,0.15)]'
              )}
            >
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-2 rounded-xl text-[var(--text-tertiary)] hover:bg-[var(--surface-hover)] transition-colors"
              >
                <X size={18} weight="bold" />
              </button>
              <ModalBody onClose={onClose} />
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

function ModalBody({ onClose }: { onClose: () => void }) {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-[var(--primary-subtle)] flex items-center justify-center">
        <WarningCircle size={32} weight="duotone" className="text-[var(--primary)]" />
      </div>
      <h3 className="text-lg font-bold text-[var(--text-primary)]">
        Небольшая пауза
      </h3>
      <p className="mt-2 text-sm text-[var(--text-secondary)] leading-relaxed">
        Чтобы не подводить Вас, мы временно приостановили приём заказов.
      </p>
      <button
        onClick={onClose}
        className={cn(
          'mt-6 w-full py-3.5 rounded-2xl font-semibold text-sm',
          'bg-[var(--primary)] text-white',
          'hover:bg-[var(--primary-hover)] transition-colors',
          'active:scale-[0.98] transition-transform'
        )}
      >
        Хорошо
      </button>
    </div>
  );
}
